import React, { useEffect, useState, useRef } from "react";
import { Box, Container, Grid, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useParams } from "react-router-dom";
import { Pay } from "../../../../models/pay";
import { Lend } from "../../../../models/lend";
import { TruckDeliveryOutline } from "mdi-material-ui";

interface ShowAlerts {
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}
const Invoice: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams();
  const [pays, setPays] = useState<Pay | null>(null);
  const [lend, setLend] = useState<Lend | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true); // Estado de carga // Estado de carga
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: paysData } = await axios.get(`pays/${id}`);
        setPays(paysData);
        if (paysData.lend.id) {
          const { data: lendData } = await axios.get(`lends/${paysData.lend.id}`);
          setLend(lendData);
        }
      } catch (error) {
        //console.log('Error fetching data:', 'error');
      } finally {
      }
    };
    fetchData();
  }, [id, showAlert, showLoading, hideLoading]);

  const formattedDate = pays?.date.slice(0, 10).replaceAll("-", "");
  const formattedTime = pays?.date.slice(11, 19).replaceAll(":", "").replace(".", "");
  const tcktNmbr = `${formattedDate}${formattedTime}-${pays?.id}`;
  const hidePdfButtons = () => {
    document.querySelectorAll('.pdf-hide').forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });
  };

  // Función para mostrar los botones nuevamente
  const showPdfButtons = () => {
    document.querySelectorAll('.pdf-hide').forEach((element) => {
      (element as HTMLElement).style.display = 'inline-block';
    });
  };
  async function handlePrint(clientFullName: string) {
    if (invoiceRef.current && lend?.client?.fullname && pays) {
      const clientName = lend.client.fullname.replace(/\s+/g, '_');
      const fileName = `${clientName}-Recibo-${tcktNmbr}.pdf`;

      // Oculta los botones antes de generar el PDF
      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));
      // Esperar un momento para asegurar que los elementos se hayan ocultado
      const pdfWidth = 75; // Ancho en mm (7.5 cm)

      // Genera el canvas
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4, // Ajusta la escala para mejorar la calidad
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      // Calcular la altura en función del contenido
      const imgData = canvas.toDataURL('image/png');
      const imgHeight = (canvas.height * pdfWidth) / canvas.width; // Mantener la proporción

      // Crear el PDF con la altura dinámica calculada
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight], // Ajustar el alto según el contenido
      });

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
      pdf.save(fileName);

      // Volver a mostrar los botones después de generar el PDF
      showPdfButtons();
    }
  }

  async function handleSharePDF(clientFullName: string) {
    if (invoiceRef.current && clientFullName) {
      const clientName = clientFullName.replace(/\s+/g, '_');
      const fileName = `${clientName}-Recibo-${tcktNmbr}.pdf`;

      // Oculta los botones antes de generar el PDF
      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));
      // Esperar un momento para asegurar que los elementos se hayan ocultado
      const pdfWidth = 75; // Ancho en mm (7.5 cm)

      // Genera el canvas
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4, // Ajusta la escala para mejorar la calidad
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      // Calcular la altura en función del contenido
      const imgData = canvas.toDataURL('image/png');
      const imgHeight = (canvas.height * pdfWidth) / canvas.width; // Mantener la proporción

      // Crear el PDF con la altura dinámica calculada
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight], // Ajustar el alto según el contenido
      });

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');

      const pdfBlob = pdf.output('blob');

      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], fileName, { type: "application/pdf" })] })) {
          const file = new File([pdfBlob], fileName, { type: "application/pdf" });
          await navigator.share({
            title: "Compartir PDF",
            text: "Aquí tienes el PDF generado.",
            files: [file],
          });
        } else {
          const pdfUrl = URL.createObjectURL(pdfBlob);
          window.open(pdfUrl, '_blank');
          showAlert("El navegador no soporta la funcionalidad de compartir archivos.", "info");
        }
      } catch (error) {
        console.error('Error al generar el PDF o al compartir:', error);
        showAlert('Error al generar el PDF o compartir el archivo. Intenta nuevamente.', 'error');
      }

      // Volver a mostrar los botones después de generar el PDF
      showPdfButtons();
    } else {
      showAlert("Información del cliente no disponible o contenido vacío. No se puede generar el PDF.", "warning");
    }
  }


  return (
    <div className="page-content" id="Invoice" ref={invoiceRef}>
      {/* <Box sx={{ width: isMobile ? '100%' : '100%', margin: '0 auto' }}> */}
      <Box sx={{ width: '375px', margin: '0 auto', height: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
              <Typography gutterBottom>
                Recibo #{tcktNmbr}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
              <Typography variant="h5" gutterBottom>
                Comprobante de Pago
              </Typography>
            </Box>

            <hr />
            <Grid container spacing={2}>
              <Grid item sm={6}>
                {lend?.client && (
                  <dl>
                    <dt><strong>Nombre:</strong> {lend.client.fullname}</dt>
                    <dt><strong>Identificación:</strong> {lend.client.idnumber}</dt>
                    <dt><strong>Dirección:</strong> {lend.client.mainaddress}</dt>
                  </dl>
                )}
              </Grid>
              <Grid item sm={6} textAlign="right">
                <strong>Fecha de Pago:</strong>
                <br />
                {pays?.date} {pays?.date.slice(11, 16)}
              </Grid>
            </Grid>
            <hr />
            <Typography variant="h6" gutterBottom>Resumen de Pago</Typography>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">Valor Total: {lend?.total_value}</TableCell>
                    <TableCell align="right">Tipo de Pago: Efectivo</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Cuota #{pays?.id}</TableCell>
                    <TableCell align="right">Valor cuota: ${pays?.value}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <hr />
            <Grid container spacing={2}>
              <Grid item sm={6} sx={{ mt: 2 }}>
                <strong>Generado por:</strong> {lend?.user?.username || 'Desconocido'}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid item xs={12} display="flex" justifyContent={isMobile ? 'center' : 'flex-end'} flexDirection={isMobile ? 'column' : 'row'} mt={2}>
          <Button className="pdf-hide" variant="contained" sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0 }} onClick={() => handlePrint(lend?.client?.fullname || 'Cliente')}>
            Descargar
          </Button>
          <Button className="pdf-hide" variant="contained" onClick={() => handleSharePDF(lend?.client?.fullname || 'Cliente')}>
            Compartir
          </Button>
        </Grid>
      </Box>

    </div>
  );

};

export default Invoice;
